import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num } from './expr';
import { Polynomial } from './polynomial';

export interface Factor {
  factor: Polynomial;
  multiplicity: number;
}

export type FactorStep =
  | { kind: 'gcd-extracted'; gcd: Polynomial }
  | { kind: 'rational-root-found'; root: Fraction; quotient: Polynomial }
  | { kind: 'grouping'; pairs: [Polynomial, Polynomial][] }
  | {
      kind: 'special-product';
      form: 'difference-of-squares' | 'sum-of-cubes' | 'difference-of-cubes' | 'perfect-square';
      result: readonly Factor[];
    }
  | {
      kind: 'quadratic-formula';
      a: Fraction;
      b: Fraction;
      c: Fraction;
      discriminant: Fraction;
      roots: Expr[];
    }
  | { kind: 'irreducible'; over: 'Q'; polynomial: Polynomial };

export interface FactorResult {
  factors: Factor[];
  content: Fraction;
  steps: FactorStep[];
}

export function factor(poly: Polynomial): FactorResult {
  const steps: FactorStep[] = [];
  const factors: Factor[] = [];

  // Step 1: Extract content (gcd of coefficients)
  const { content, primitive } = extractContent(poly);

  let current = primitive;

  // Step 2: Factor out common monomial (x^k)
  const { deflated, monomialMultiplicity } = factorCommonMonomial(current);
  if (monomialMultiplicity > 0) {
    factors.push({
      factor: new Polynomial([Fraction.ZERO, Fraction.ONE], poly.varName),
      multiplicity: monomialMultiplicity,
    });
    steps.push({
      kind: 'gcd-extracted',
      gcd: new Polynomial([Fraction.ZERO, Fraction.ONE], poly.varName),
    });
    current = deflated;
  }

  // Process remaining polynomial
  processPolynomial(current, factors, steps, poly.varName);

  // Step 7: Group by multiplicity
  const groupedFactors = groupFactorsByMultiplicity(factors);

  return {
    factors: groupedFactors,
    content,
    steps,
  };
}

/**
 * Extract content: find the GCD of numerators and LCM of denominators
 * Scale the polynomial to have integer coefficients with positive leading coeff
 */
function extractContent(poly: Polynomial): { content: Fraction; primitive: Polynomial } {
  if (poly.isZero) {
    return { content: Fraction.ONE, primitive: poly };
  }

  // Find GCD of numerators and LCM of denominators
  let gcdNum = 0;
  let lcmDen = 1;

  for (const coeff of poly.coeffs) {
    const n = Math.abs(coeff.num);
    const d = coeff.den;

    // GCD of numerators
    if (gcdNum === 0) {
      gcdNum = n;
    } else {
      gcdNum = gcdInteger(gcdNum, n);
    }

    // LCM of denominators
    lcmDen = lcmInteger(lcmDen, d);
  }

  if (gcdNum === 0) gcdNum = 1;

  // Content = (gcdNum / lcmDen) with sign of leading coefficient
  const leadingSign = poly.leadingCoefficient.sign;
  const content = new Fraction(leadingSign * gcdNum, lcmDen);

  // Divide out the content
  const primitive = poly.scale(content.reciprocal());

  return { content, primitive };
}

/**
 * Factor out common monomial x^k
 */
function factorCommonMonomial(poly: Polynomial): {
  deflated: Polynomial;
  monomialMultiplicity: number;
} {
  if (poly.isZero) {
    return { deflated: poly, monomialMultiplicity: 0 };
  }

  // Find the lowest-degree nonzero term
  let k = 0;
  for (let i = 0; i < poly.coeffs.length; i += 1) {
    if (poly.coeffs[i]!.num !== 0) {
      k = i;
      break;
    }
  }

  if (k === 0) {
    return { deflated: poly, monomialMultiplicity: 0 };
  }

  // Deflate by removing the first k coefficients
  const deflatedCoeffs = poly.coeffs.slice(k);
  const deflated = new Polynomial(deflatedCoeffs, poly.varName);

  return { deflated, monomialMultiplicity: k };
}

/**
 * Check if a rational p/q is a perfect square
 */
function isPerfectSquareRational(frac: Fraction): boolean {
  if (frac.num <= 0) return false;
  return isPerfectSquareInteger(frac.num) && isPerfectSquareInteger(frac.den);
}

/**
 * Check if an integer is a perfect square
 */
function isPerfectSquareInteger(n: number): boolean {
  if (n < 0) return false;
  const root = Math.sqrt(n);
  return Number.isInteger(root);
}

/**
 * Check if a rational p/q is a perfect cube
 */
function isPerfectCubeRational(frac: Fraction): boolean {
  if (frac.num === 0) return true;
  const absNum = Math.abs(frac.num);
  return isPerfectCubeInteger(absNum) && isPerfectCubeInteger(frac.den);
}

/**
 * Check if an integer is a perfect cube
 */
function isPerfectCubeInteger(n: number): boolean {
  if (n === 0) return true;
  const absN = Math.abs(n);
  const root = Math.round(Math.cbrt(absN));
  return root * root * root === absN;
}

/**
 * Get the integer square root (for perfect squares)
 */
function integerSquareRoot(n: number): number {
  if (n < 0) throw new Error('integerSquareRoot: negative input');
  return Math.floor(Math.sqrt(n) + 0.5);
}

/**
 * Get the integer cube root
 */
function integerCubeRoot(n: number): number {
  const signVal = n < 0 ? -1 : 1;
  return signVal * Math.round(Math.cbrt(Math.abs(n)));
}

/**
 * Check for and factor special products
 */
function trySpecialProduct(poly: Polynomial): { factors: Factor[]; steps: FactorStep[] } | null {
  const steps: FactorStep[] = [];
  const factors: Factor[] = [];

  if (poly.degree === 2 && poly.coeffs.length === 3) {
    // Check for perfect square: a²x² + 2abx + b² or a²x² - 2abx + b²
    const [c0, c1, c2] = [poly.coeffs[0]!, poly.coeffs[1]!, poly.coeffs[2]!];

    // Check a² and b²
    if (isPerfectSquareRational(c2.abs) && isPerfectSquareRational(c0.abs)) {
      const a = new Fraction(integerSquareRoot(c2.num), integerSquareRoot(c2.den));
      const b = new Fraction(integerSquareRoot(Math.abs(c0.num)), integerSquareRoot(c0.den));

      // Check if c1 = ±2ab
      const twoAb = a.mul(b).mul(new Fraction(2, 1));
      if (c1.equals(twoAb) || c1.equals(twoAb.neg())) {
        // Perfect square trinomial
        const signB = c1.sign > 0 ? 1 : -1;
        const linearFactor = new Polynomial([b.mul(new Fraction(signB, 1)), a], poly.varName);
        factors.push({ factor: linearFactor, multiplicity: 2 });
        steps.push({
          kind: 'special-product',
          form: 'perfect-square',
          result: [...factors],
        });
        return { factors, steps };
      }
    }

    // Check for difference of squares: a²x² - b² (constant term is NEGATIVE)
    if (
      c1.num === 0 &&
      isPerfectSquareRational(c2.abs) &&
      isPerfectSquareRational(c0.abs) &&
      c0.sign < 0
    ) {
      const a = new Fraction(integerSquareRoot(c2.num), integerSquareRoot(c2.den));
      const b = new Fraction(integerSquareRoot(Math.abs(c0.num)), integerSquareRoot(c0.den));

      // Factors: (ax - b)(ax + b)
      const factor1 = new Polynomial([b.neg(), a], poly.varName);
      const factor2 = new Polynomial([b, a], poly.varName);

      factors.push({ factor: factor1, multiplicity: 1 });
      factors.push({ factor: factor2, multiplicity: 1 });

      steps.push({
        kind: 'special-product',
        form: 'difference-of-squares',
        result: [...factors],
      });

      return { factors, steps };
    }
  }

  if (poly.degree === 3 && poly.coeffs.length === 4) {
    // Check for sum or difference of cubes: a³x³ ± b³
    const [c0, , , c3] = [poly.coeffs[0]!, poly.coeffs[1]!, poly.coeffs[2]!, poly.coeffs[3]!];

    // Middle coefficients should be zero
    if (poly.coeffs[1]!.num === 0 && poly.coeffs[2]!.num === 0) {
      if (isPerfectCubeRational(c3) && isPerfectCubeRational(c0)) {
        const a = new Fraction(integerCubeRoot(c3.num), integerCubeRoot(c3.den));
        const b = new Fraction(
          integerCubeRoot(Math.abs(c0.num)),
          integerCubeRoot(Math.abs(c0.den)),
        );

        if (c0.sign > 0) {
          // Sum of cubes: a³x³ + b³ = (ax + b)(a²x² - abx + b²)
          const linearFactor = new Polynomial([b, a], poly.varName);
          const a2 = a.mul(a);
          const ab = a.mul(b);
          const b2 = b.mul(b);
          const quadraticFactor = new Polynomial([b2, ab.neg(), a2], poly.varName);

          factors.push({ factor: linearFactor, multiplicity: 1 });
          factors.push({ factor: quadraticFactor, multiplicity: 1 });

          steps.push({
            kind: 'special-product',
            form: 'sum-of-cubes',
            result: [...factors],
          });

          return { factors, steps };
        } else {
          // Difference of cubes: a³x³ - b³ = (ax - b)(a²x² + abx + b²)
          const linearFactor = new Polynomial([b.neg(), a], poly.varName);
          const a2 = a.mul(a);
          const ab = a.mul(b);
          const b2 = b.mul(b);
          const quadraticFactor = new Polynomial([b2, ab, a2], poly.varName);

          factors.push({ factor: linearFactor, multiplicity: 1 });
          factors.push({ factor: quadraticFactor, multiplicity: 1 });

          steps.push({
            kind: 'special-product',
            form: 'difference-of-cubes',
            result: [...factors],
          });

          return { factors, steps };
        }
      }
    }
  }

  return null;
}

/**
 * Get all divisors of an integer
 */
function getDivisors(n: number): number[] {
  const divisors: number[] = [];
  const absN = Math.abs(n);

  if (absN === 0) {
    return [0];
  }

  for (let i = 1; i * i <= absN; i += 1) {
    if (absN % i === 0) {
      divisors.push(i);
      if (i !== absN / i) {
        divisors.push(absN / i);
      }
    }
  }

  return divisors.sort((a, b) => a - b);
}

/**
 * Generate all rational root candidates p/q from the polynomial
 */
function getRationalRootCandidates(poly: Polynomial, maxCount: number = 200): Fraction[] {
  if (poly.isZero || poly.degree < 1) {
    return [];
  }

  const constantTerm = poly.coeffs[0]!;
  const leadingCoeff = poly.leadingCoefficient;

  const pDivisors = getDivisors(constantTerm.num);
  const qDivisors = getDivisors(leadingCoeff.num);

  const candidates: Fraction[] = [];

  for (const p of pDivisors) {
    for (const q of qDivisors) {
      for (const sign of [-1, 1]) {
        const frac = new Fraction(sign * p, q);
        // Check if we already have this candidate
        if (!candidates.some((c) => c.equals(frac))) {
          candidates.push(frac);
        }
        if (candidates.length >= maxCount) {
          return candidates;
        }
      }
    }
  }

  return candidates;
}

/**
 * Process a polynomial: extract factors via special products, rational roots, etc.
 */
function processPolynomial(
  poly: Polynomial,
  factors: Factor[],
  steps: FactorStep[],
  varName: string,
): void {
  if (poly.isZero || poly.degree === 0) {
    return;
  }

  if (poly.degree === 1) {
    // Linear factor
    factors.push({ factor: poly, multiplicity: 1 });
    return;
  }

  // Try special products
  const special = trySpecialProduct(poly);
  if (special !== null) {
    factors.push(...special.factors);
    steps.push(...special.steps);
    return;
  }

  // Try rational root theorem
  const candidates = getRationalRootCandidates(poly);

  for (const root of candidates) {
    if (poly.evaluate(root).num === 0) {
      // Found a root; factor out (q·x - p) where root = p/q
      const p = root.num;
      const q = root.den;

      const linearFactor = new Polynomial([new Fraction(-p, 1), new Fraction(q, 1)], varName);

      const { quotient, remainder } = poly.divmod(linearFactor);

      if (remainder.isZero) {
        factors.push({ factor: linearFactor, multiplicity: 1 });
        steps.push({
          kind: 'rational-root-found',
          root,
          quotient,
        });

        // Recurse on the quotient
        processPolynomial(quotient, factors, steps, varName);
        return;
      }
    }
  }

  // Degree 2: check for quadratic factorization
  if (poly.degree === 2 && poly.coeffs.length === 3) {
    const [c0, c1, c2] = [poly.coeffs[0]!, poly.coeffs[1]!, poly.coeffs[2]!];

    // Quadratic formula: discriminant = b² - 4ac
    const discriminant = c1.mul(c1).sub(c2.mul(c0).mul(new Fraction(4, 1)));

    if (isPerfectSquareRational(discriminant)) {
      // Two rational roots
      const discRoot = new Fraction(
        integerSquareRoot(discriminant.num),
        integerSquareRoot(discriminant.den),
      );

      const root1 = c1
        .neg()
        .add(discRoot)
        .div(c2.mul(new Fraction(2, 1)));
      const root2 = c1
        .neg()
        .sub(discRoot)
        .div(c2.mul(new Fraction(2, 1)));

      const p1 = root1.num;
      const q1 = root1.den;
      const p2 = root2.num;
      const q2 = root2.den;

      const factor1 = new Polynomial([new Fraction(-p1, 1), new Fraction(q1, 1)], varName);
      const factor2 = new Polynomial([new Fraction(-p2, 1), new Fraction(q2, 1)], varName);

      factors.push({ factor: factor1, multiplicity: 1 });
      factors.push({ factor: factor2, multiplicity: 1 });

      steps.push({
        kind: 'quadratic-formula',
        a: c2,
        b: c1,
        c: c0,
        discriminant,
        roots: [num(root1), num(root2)],
      });
    } else {
      // Irreducible quadratic
      factors.push({ factor: poly, multiplicity: 1 });
      steps.push({
        kind: 'irreducible',
        over: 'Q',
        polynomial: poly,
      });
    }
  } else {
    // Irreducible polynomial
    factors.push({ factor: poly, multiplicity: 1 });
    steps.push({
      kind: 'irreducible',
      over: 'Q',
      polynomial: poly,
    });
  }
}

/**
 * Group factors by structural equality and sum multiplicities
 */
function groupFactorsByMultiplicity(rawFactors: Factor[]): Factor[] {
  const groupMap = new Map<string, { factor: Polynomial; multiplicity: number }>();

  for (const fac of rawFactors) {
    const key = fac.factor.coeffs.map((c) => c.toString()).join(',');
    const existing = groupMap.get(key);

    if (existing) {
      existing.multiplicity += fac.multiplicity;
    } else {
      groupMap.set(key, { factor: fac.factor, multiplicity: fac.multiplicity });
    }
  }

  // Sort deterministically: by degree, then by leading coefficient
  const result = Array.from(groupMap.values());
  result.sort((a, b) => {
    const degDiff = a.factor.degree - b.factor.degree;
    if (degDiff !== 0) return degDiff;

    return a.factor.leadingCoefficient.cmp(b.factor.leadingCoefficient);
  });

  return result;
}

/**
 * GCD of two positive integers
 */
function gcdInteger(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);

  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }

  return x;
}

/**
 * LCM of two positive integers
 */
function lcmInteger(a: number, b: number): number {
  return (a / gcdInteger(a, b)) * b;
}
